import type { IChatMessage } from './ChatSlice.js'

/**
 * W-1 — a node in the TREE conversation. The tree (a `Record<id, ChatNode>` + a `currentNodeId`) is the source
 * of truth; the linear `messages` the UI renders is the derived root→current PATH (see {@link pathToRoot}).
 * A `parentId` of `null` marks the single root. Branching = a node with more than one child.
 */
export interface ChatNode {
  id: string
  parentId: string | null
  message: IChatMessage
}

/** The node store: id → node. Object key insertion order is the stable child/branch order. */
export type NodeMap = Record<string, ChatNode>

/** Direct children of `parentId`, in insertion (branch-creation) order. `parentId=null` yields the root(s). */
export function childrenOf(nodes: NodeMap, parentId: string | null): ChatNode[] {
  return Object.values(nodes).filter((n) => n.parentId === parentId)
}

/**
 * The active PATH: walk `currentNodeId` up to the root and reverse, so the result reads root→current — exactly
 * the linear conversation the UI renders and the agent receives as `history`. Cycle-guarded (a corrupt blob
 * can't hang the walk); returns `[]` when `currentNodeId` is null or dangling.
 */
export function pathToRoot(nodes: NodeMap, currentNodeId: string | null): ChatNode[] {
  const path: ChatNode[] = []
  const seen = new Set<string>()
  let id: string | null = currentNodeId
  while (id) {
    const node: ChatNode | undefined = nodes[id]
    if (!node || seen.has(id)) break
    seen.add(id)
    path.push(node)
    id = node.parentId
  }
  return path.reverse()
}

/** Every node that shares this node's parent (including the node itself) — the branch set at that fork. */
export function siblingsOf(nodes: NodeMap, node: ChatNode): ChatNode[] {
  return childrenOf(nodes, node.parentId)
}

/**
 * Follow a node's chain down to a leaf, always taking the LATEST (last-created) child at each fork, so
 * switching to a branch lands on the most recent tip of that branch rather than a stale interior node.
 */
export function deepestLeaf(nodes: NodeMap, nodeId: string): string {
  let id = nodeId
  const seen = new Set<string>()
  for (;;) {
    if (seen.has(id)) return id
    seen.add(id)
    const kids = childrenOf(nodes, id)
    if (kids.length === 0) return id
    id = kids[kids.length - 1].id
  }
}
